import { t } from 'i18next';
import {
  CallMetricChartPoint,
  CallMetrics,
  CallbackQueue,
  CallbackQueueDepartment,
  Insight,
  InsightLanguageDistribution,
  InsightOverallSatisfaction,
} from '@/services/dashboard-service/dashboard.service.types';
import {
  KpiTileModel,
  asCount,
  asOptionalCount,
  toDelta,
  toTrendSeries,
  trendPointsAreHourly,
} from '@/pages/dashboard/dashboard.utils';
import { MINUTES_SAVED_PER_CASE } from '@/pages/dashboard/dashboard.constants';
import { StatTableRow } from '@/components/stat-table/stat-table.types';
import { MeterTone } from '@/components/meter/meter.types';
import { rankAndFold } from '@/utils/rank';
import { averageRating, formatScoreOutOfFive, scoreDeltaToFive } from '@/utils/score';
import {
  CALLBACK_DEPARTMENT_ROWS,
  TOPIC_BAR_LIMIT,
  WEEKDAY_KEYS,
  WEEKDAY_UNAVAILABLE_KEY,
  WeekdayUnavailable,
  deltaCaption,
} from './statistics.constants';

export { WEEKDAY_UNAVAILABLE_KEY, asCount, deltaCaption };

type KpiArgs = {
  metrics?: CallMetrics;
  satisfaction?: InsightOverallSatisfaction;
};

/**
 * The mockup's four tiles. Labels are written from what each metric counts rather
 * than from its payload key, and the three delta units stay apart: a count moves by a
 * relative percent, a share by percentage points, a rating on its own scale.
 */
export function buildStatisticsKpiTiles({ metrics, satisfaction }: KpiArgs): KpiTileModel[] {
  const rated = Boolean(satisfaction?.responses);
  const rating = averageRating(satisfaction);
  // Both figures arrive on 0-100, so the change takes the same scaling as the value it
  // sits under: rendering it raw beside a `/ 5` figure would be twenty times too large.
  const ratingChange = rated
    ? toDelta(scoreDeltaToFive(satisfaction?.change ?? 0, 'scale100'), '')
    : undefined;
  const outOfHours = metrics?.out_of_hours_calls;

  return [
    {
      key: 'calls',
      label: t('statistics:kpis.calls'),
      value: asCount(metrics?.inbound_calls.value ?? 0),
      delta: toDelta(metrics?.inbound_calls.change ?? null),
    },
    {
      key: 'effectiveness',
      label: t('statistics:kpis.effectiveness.label'),
      value: `${Math.round(metrics?.ai_resolution.current_percent ?? 0)}`,
      unit: '%',
      // The denominator is answered calls, not every call, and the tile has to say so.
      note: t('statistics:kpis.effectiveness.note'),
      delta: toDelta(metrics?.ai_resolution.change ?? null, ' p.p.'),
    },
    {
      key: 'rating',
      label: t('statistics:kpis.rating'),
      value: formatScoreOutOfFive(rating),
      unit: '/ 5',
      delta: ratingChange,
    },
    {
      key: 'after-hours',
      label: t('statistics:kpis.afterHours'),
      value: asOptionalCount(outOfHours?.value),
      delta: outOfHours ? toDelta(outOfHours.change) : undefined,
    },
  ];
}

/** Whole hours and remaining minutes, as the mockup writes the recovered time. */
function asHoursAndMinutes(minutes: number): string {
  // Rounded before splitting: a fractional input would otherwise reach the minutes
  // column as `28.24 min`.
  const whole = Math.round(minutes);

  return t('statistics:impact.timeRecovered', {
    hours: Math.floor(whole / 60),
    minutes: whole % 60,
  });
}

/**
 * Five real rows. Row one counts every call in the window: the metric carries no
 * direction, so calling it a diversion from the front desk would be false for every
 * call that was handed on.
 */
export function buildImpactRows({ metrics }: { metrics?: CallMetrics }): StatTableRow[] {
  const resolved = metrics?.ai_resolution.value ?? 0;

  return [
    {
      label: t('statistics:impact.rows.callsReceived'),
      values: [asCount(metrics?.inbound_calls.value ?? 0)],
    },
    {
      label: t('statistics:impact.rows.casesClosed'),
      values: [asCount(resolved)],
      positive: true,
    },
    {
      label: t('statistics:impact.rows.timeRecovered'),
      values: [asHoursAndMinutes(resolved * MINUTES_SAVED_PER_CASE)],
      positive: true,
    },
    {
      label: t('statistics:impact.rows.afterHours'),
      values: [asOptionalCount(metrics?.out_of_hours_calls?.value)],
    },
    // Not tinted: a missed call is not a good outcome, whatever the mockup's green says.
    { label: t('statistics:impact.rows.missed'), values: [asCount(metrics?.abandon.value ?? 0)] },
  ];
}

/**
 * Named targets by volume, then one fold row, then the cases pointed at nobody.
 * The fold is `total` minus what is listed, so an unrecognised kind cannot go missing.
 */
export function toCallbackRows(queue?: CallbackQueue): StatTableRow[] {
  if (!queue) return [];

  const isNamedUnit = (row: CallbackQueueDepartment) => row.kind === 'unit' && !!row.department;
  const named = [...queue.by_department]
    .filter(isNamedUnit)
    .sort((a, b) => b.cases - a.cases || (a.department ?? '').localeCompare(b.department ?? ''));

  const listed = named.slice(0, CALLBACK_DEPARTMENT_ROWS);

  const rows: StatTableRow[] = listed.map((row) => ({
    label: row.department as string,
    values: [asCount(row.cases)],
  }));

  const folded = queue.total - queue.unassigned - listed.reduce((sum, row) => sum + row.cases, 0);

  if (folded > 0)
    rows.push({ label: t('statistics:callbacks.rows.other'), values: [asCount(folded)] });

  if (queue.unassigned > 0) {
    rows.push({
      label: t('statistics:callbacks.rows.unassigned'),
      values: [asCount(queue.unassigned)],
    });
  }

  return rows;
}

/**
 * The window, the total and the urgency split, under a table that shows none of them.
 * The window is named because the aggregate counts cases raised in it, not all still open.
 * `range` is the phrase for it, already worded (`metrics:range.in.*`).
 */
export function callbackUrgencyCaption(queue?: CallbackQueue, range?: string): string | null {
  if (!queue) return null;

  const raised = range ? `${t('statistics:callbacks.raised', { range })} · ` : '';
  const total = t('metrics:units.cases', { count: Math.round(queue.total) });
  // A case that arrived without an urgency is not a low-urgency case, so it has its own word.
  const split = queue.by_urgency
    .filter((row) => row.cases > 0)
    .map((row) =>
      t(`statistics:callbacks.urgency.${row.urgency ?? 'unset'}`, { count: Math.round(row.cases) })
    );

  return split.length ? `${raised}${total} · ${split.join(', ')}` : `${raised}${total}`;
}

export type WeekdaySeries =
  | { available: false; reason: WeekdayUnavailable }
  | { available: true; values: number[] };

/** Monday-first index for a `YYYY-MM-DD` bucket, read off the date as written. */
function weekdayIndex(date: string): number | null {
  const parts = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);

  if (!parts) return null;

  const day = new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3])).getDay();

  return (day + 6) % 7;
}

/**
 * Assistant-handled calls averaged per weekday. Hour buckets cover a rolling day
 * that crosses midnight, so they cannot answer "which weekday is busiest" and the
 * card says so instead of drawing two bars out of seven.
 */
export function toWeekdaySeries(chart: CallMetricChartPoint[]): WeekdaySeries {
  if (!chart.length) return { available: false, reason: 'empty' };
  if (trendPointsAreHourly(chart)) return { available: false, reason: 'hourly' };

  const totals = WEEKDAY_KEYS.map(() => 0);
  const occurrences = WEEKDAY_KEYS.map(() => 0);

  toTrendSeries(chart).forEach((point) => {
    const index = weekdayIndex(point.date);

    if (index === null) return;

    totals[index] += point.value;
    occurrences[index] += 1;
  });

  // The axis carries one bucket per period whether or not calls landed, so an all-zero
  // series is either a window with no calls or a tenant whose calls are all agent-handled:
  // `ai - agent` clamps to zero for the second. The raw payload separates them.
  if (!totals.some((total) => total > 0)) {
    const anyCalls = chart.some((point) => point.ai > 0 || point.agent > 0);

    return { available: false, reason: anyCalls ? 'not-measured' : 'empty' };
  }

  // Divided by how often each weekday actually occurred, not by the mockup's fixed
  // `30 / 7`: a 30-day window holds four of some weekdays and five of others.
  return {
    available: true,
    values: totals.map((total, index) =>
      occurrences[index] ? Math.round(total / occurrences[index]) : 0
    ),
  };
}

export type BarRow = {
  label: string;
  caption: string;
  percent: number;
};

/**
 * Call volume per topic, folded at the donut's limit so both screens show the same
 * topics. Bars are normalised to the busiest topic, as the mockup draws them. The
 * caption carries the count and nothing else: per-topic bot effectiveness is not
 * derivable from any endpoint, and a fabricated share beside a real count would cost
 * the card its real status.
 */
export function toTopicBars(topics: Insight): BarRow[] {
  const ranked = rankAndFold(topics, TOPIC_BAR_LIMIT);
  const busiest = Math.max(...ranked.map((entry) => entry.count), 0);

  return ranked.map((entry) => ({
    label: entry.label,
    caption: t('metrics:units.calls', { count: Math.round(entry.count) }),
    percent: busiest ? Math.round((entry.count / busiest) * 100) : 0,
  }));
}

export type LanguageRow = {
  label: string;
  count: number;
  percent: number;
  tone: MeterTone;
};

/**
 * The payload's own catch-all language bucket, unrelated to `rankAndFold`'s fold label. The
 * label stays the payload's (it is matched on); the screen words it with `metrics:other`.
 */
export const LANGUAGE_OVERFLOW_LABEL = 'Other';

const SERIES_TONES: MeterTone[] = [
  'series1',
  'series2',
  'series3',
  'series4',
  'series5',
  'series6',
];

/** Pinned rather than positional, so the catch-all keeps one colour as the set varies. */
const OVERFLOW_TONE: MeterTone = 'series7';

/**
 * Whatever the window actually detected. A bucket of zero or null is a language that
 * did not appear, so it is dropped rather than drawn as an empty bar, and the catch-all
 * stays last however large it is. Colour follows position, not language: the payload's
 * language set varies between tenants, so a fixed per-language map cannot work.
 * `language_flag_url` is read and discarded: the mockup's rows carry a name and a bar,
 * no flag, so rendering one would be inventing UI.
 */
export function toLanguageRows(payload: InsightLanguageDistribution): LanguageRow[] {
  const entries = Object.entries(payload)
    .map(([label, entry]) => ({
      label,
      count: typeof entry === 'number' ? entry : (entry?.count ?? 0),
    }))
    .filter((entry) => entry.count > 0);

  const busiest = Math.max(...entries.map((entry) => entry.count), 0);

  return entries
    .sort((a, b) => {
      if (a.label === LANGUAGE_OVERFLOW_LABEL) return 1;
      if (b.label === LANGUAGE_OVERFLOW_LABEL) return -1;

      return b.count - a.count || a.label.localeCompare(b.label);
    })
    .map((entry, index) => ({
      ...entry,
      percent: busiest ? Math.round((entry.count / busiest) * 100) : 0,
      // Past the palette a row takes the catch-all tone rather than wrapping onto a used one.
      tone:
        entry.label === LANGUAGE_OVERFLOW_LABEL
          ? OVERFLOW_TONE
          : (SERIES_TONES[index] ?? OVERFLOW_TONE),
    }));
}
